/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form } from 'antd';
import dayjs from 'dayjs';

import { Node } from '@/client';
import { CardCollapse } from '@/components/CardCollapse';
import { IncomingData } from '@/components/Node/IncomingData';
import { NodeViewPageCard } from '@/components/Node/NodeViewPageCard';
import { RelativeTime } from '@/components/RelativeTime';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import { Tooltip } from '@/primitives/Tooltip';

import { AssignConfig } from './AssignConfig';
import { NodeConfigDetails } from './NodeConfigDetails';

export type NodeConfigStateProps = {
  node: Node;
  assignNewNodeConfig: (node: Node) => void;
  assignNewConfigRevision: (node: Node) => void;
  getNodeConfigPath: (node: Node) => string;
  getNodeConfigPreviousPath: (node: Node) => string;
  getNodeConfigRevisionPath: (node: Node) => string;
  getNodeConfigPreviousRevisionPath: (node: Node) => string;
};

export function NodeConfigState({
  node,
  assignNewNodeConfig,
  assignNewConfigRevision,
  getNodeConfigPath,
  getNodeConfigPreviousPath,
  getNodeConfigRevisionPath,
  getNodeConfigPreviousRevisionPath,
}: NodeConfigStateProps) {
  const { t } = useMimicTranslation('node');

  const isPending = node.requestedRevisionState.status === 'pending';
  const version = node.nodeVersion?.split('+')[0];
  const buildNumber = node.nodeVersion?.split('+')[1];

  return (
    <CardCollapse>
      <NodeViewPageCard bodyStyles={{ paddingBottom: '12px', paddingTop: '16px' }}>
        <Form layout="vertical">
          <Flex vertical gap="sm">
            <Flex gap="xs" vertical justify="space-between">
              <Text type="default" style={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {t('nodeConfiguration')}
              </Text>
              <Text type="subtle" size="md">
                {t('lastUpdatedOn')} {node.dateUpdated && dayjs(node.dateUpdated).utc().format('D MMM YYYY HH:mm:ss')}{' '}
                UTC ({node.dateUpdated && <RelativeTime date={node.dateUpdated} />})
              </Text>
            </Flex>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={18}>
                <div style={{ marginRight: '48px' }}>
                  <IncomingData
                    dataTestId="node-id"
                    label={t('node_id')}
                    loading={false}
                    text={node.id}
                    marginBottom={0}
                    mono
                    copyable
                  />
                </div>
              </Col>
              <Col xs={24} sm={6}>
                <IncomingData label={t('node_version')} loading={false} marginBottom={0} dataTestId="node-version">
                  <Flex gap="xs" align="center">
                    <Text mono>{version}</Text>
                    {buildNumber && (
                      <Tooltip title={`Build ${buildNumber}`}>
                        <span
                          style={{
                            fontSize: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            padding: '1px 6px',
                            color: 'rgba(0, 0, 0, 0.65)',
                            cursor: 'pointer',
                          }}
                        >
                          +{buildNumber}
                        </span>
                      </Tooltip>
                    )}
                  </Flex>
                </IncomingData>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={6}>
                <IncomingData
                  label={t('hostname')}
                  loading={false}
                  text={node.hostname}
                  marginBottom={0}
                  dataTestId="node-hostname"
                  mono
                />
              </Col>
            </Row>
          </Flex>
        </Form>
      </NodeViewPageCard>
      {node.isOperating || isPending ? (
        <NodeConfigDetails
          node={node}
          title={t('currentConfiguration')}
          lastUpdatedByName={node.appliedRevisionState?.assignedBy?.displayName}
          lastUpdateByDate={node.appliedRevisionState?.statusDate}
          nodeConfigName={node.appliedRevisionState?.nodeConfig?.name}
          nodeConfigId={node.appliedRevisionState?.nodeConfig?.id}
          revisionNumber={node.appliedRevisionState?.configRevision?.revisionNumber}
          revisionDescription={node.appliedRevisionState?.configRevision?.description}
          revisionId={node.appliedRevisionState?.configRevision?.id}
          assignNewNodeConfig={assignNewNodeConfig}
          assignNewConfigRevision={assignNewConfigRevision}
          getNodeConfigPath={getNodeConfigPath}
          getNodeConfigRevisionPath={getNodeConfigRevisionPath}
        />
      ) : (
        <AssignConfig assignNewNodeConfig={() => assignNewNodeConfig(node)} node={node} />
      )}
      {node.appliedRevisionState?.nodeConfig?.mode === 'maintenance' ? (
        <NodeConfigDetails
          node={node}
          title={t('previousConfiguration')}
          lastUpdatedByName={node.previousRevisionState?.assignedBy?.displayName}
          lastUpdateByDate={node.previousRevisionState?.statusDate}
          nodeConfigName={node.previousRevisionState?.nodeConfig?.name}
          nodeConfigId={node.previousRevisionState?.nodeConfig?.id}
          revisionNumber={node.previousRevisionState?.configRevision?.revisionNumber}
          revisionDescription={node.previousRevisionState?.configRevision?.description}
          revisionId={node.previousRevisionState?.configRevision?.id}
          assignNewNodeConfig={assignNewNodeConfig}
          assignNewConfigRevision={assignNewConfigRevision}
          getNodeConfigPath={getNodeConfigPreviousPath}
          getNodeConfigRevisionPath={getNodeConfigPreviousRevisionPath}
        />
      ) : null}
    </CardCollapse>
  );
}
